// src/pages/agent/Reports.js
// Outreach report — team-shareable view of everyone she's texted, who
// answered, who bounced, and who's still queued. Defaults to the JV tag
// but filterable to any tag.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { PageHeader, Card, Btn, Field, Input, Pill, BURG, BURG_DK, GOLD, GOLD_PALE, CREAM, MID, LIGHT, BORDER } from '../../components/agent/UI';

const STATUS_COLORS = {
  contacted: 'green',
  pending: 'gold',
  no_valid_contact: 'red',
  opted_out: 'gray'
};

const STATUS_LABEL = {
  contacted: 'Contacted',
  pending: 'Pending',
  no_valid_contact: 'No valid contact',
  opted_out: 'Opted out'
};

export default function Reports() {
  const { profile } = useAgentAuth();
  const [tagFilter, setTagFilter] = useState('all');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!profile?.uid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Resolve pipeline owner — admin uses own, agent uses invitedBy.
      let pipelineUid = profile.uid;
      if (profile.role !== 'admin') {
        pipelineUid = profile.invitedByUid || null;
        if (!pipelineUid) {
          try {
            const aSnap = await getDocs(query(collection(db, 'agents'),
              where('role', '==', 'admin'), where('status', '==', 'active'), limit(1)));
            if (!aSnap.empty) pipelineUid = aSnap.docs[0].id;
          } catch {}
        }
        if (!pipelineUid) pipelineUid = profile.uid;
      }
      // Full pull — every lead in the pipeline. Filtering happens client-side.
      const snap = await getDocs(collection(db, 'agents', pipelineUid, 'leads'));
      if (cancelled) return;
      setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [profile?.uid, profile?.role, profile?.invitedByUid]);

  const rows = useMemo(() => leads.map(l => {
    const owner = (l.owners || [])[0] || {};
    const attempts = Array.isArray(l.outreachAttempts) ? l.outreachAttempts : [];
    const last = attempts[attempts.length - 1] || null;
    const lastPhone = last?.phone || owner.phone || '';
    const phoneStatus = (l.phoneStatus || {})[lastPhone] || null;
    const jv = computeJvView(l);
    return {
      id: l.id,
      addr: [l.address?.line1, l.address?.city, l.address?.state, l.address?.zip].filter(Boolean).join(', '),
      ownerName: owner.name || 'Property Owner',
      primaryPhone: owner.phone || '',
      lastPhone,
      phoneStatus,
      status: l.outreachStatus || 'pending',
      attemptCount: attempts.length,
      lastAttemptAt: l.lastOutreachAttemptAt?.toDate?.() || null,
      lastError: last?.ok === false ? (last.error || 'unknown') : null,
      city: l.address?.city || '',
      state: l.address?.state || '',
      tags: Array.isArray(l.tags) ? l.tags : [],
      leadStatus: l.status || 'New',
      jvStatus: jv.status,
      jvColor: jv.color,
      jvNotes: jv.notes
    };
  }), [leads]);

  // All distinct tags across the pipeline, sorted (JV first if present).
  const allTags = useMemo(() => {
    const set = new Set();
    rows.forEach(r => r.tags.forEach(t => set.add(t)));
    const arr = [...set];
    arr.sort((a, b) => (a === 'JV' ? -1 : b === 'JV' ? 1 : a.localeCompare(b)));
    return arr;
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (tagFilter !== 'all' && !r.tags.includes(tagFilter)) return false;
      return true;
    });
  }, [rows, statusFilter, tagFilter]);

  // Status counts scoped to the CURRENT tag filter (so a "JV" tag shows
  // per-status counts for JV only, and "All tags" shows global counts).
  const counts = useMemo(() => {
    const base = tagFilter === 'all' ? rows : rows.filter(r => r.tags.includes(tagFilter));
    const c = { all: base.length, contacted: 0, pending: 0, no_valid_contact: 0, opted_out: 0 };
    base.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows, tagFilter]);

  const exportCsv = () => {
    const header = ['Address', 'City', 'State', 'Owner', 'Primary Phone', 'Phone Contacted', 'Phone Delivery Status', 'Outreach Status', 'Attempts', 'Last Attempt', 'Last Error', 'Tags', 'Lead Status'];
    const csv = [header.join(',')].concat(
      filtered.map(r => [
        r.addr, r.city, r.state, r.ownerName, r.primaryPhone, r.lastPhone,
        r.phoneStatus || '', r.status, r.attemptCount,
        r.lastAttemptAt ? r.lastAttemptAt.toISOString() : '',
        r.lastError || '',
        r.tags.join('; '),
        r.leadStatus
      ].map(v => {
        const s = String(v == null ? '' : v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    const suffix = [
      tagFilter !== 'all' ? tagFilter : null,
      statusFilter !== 'all' ? statusFilter : null
    ].filter(Boolean).join('-');
    a.download = `outreach${suffix ? '-' + suffix : ''}-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Outreach report"
        sub="Every lead across your pipeline. Filter by tag (JV, source list, custom) and outreach status. Share with your team via CSV."
        right={<Btn variant="gold" onClick={exportCsv} disabled={!filtered.length}>⬇ Download CSV</Btn>}
      />

      <Card padded={false} style={{ marginBottom: 20 }}>
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Tag filter row */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: LIGHT, marginRight: 4 }}>Tag:</span>
            <button type="button" onClick={() => setTagFilter('all')}
              style={chipStyle(tagFilter === 'all')}>All <span style={{ opacity: 0.7 }}>{rows.length}</span></button>
            {allTags.map(tg => {
              const count = rows.filter(r => r.tags.includes(tg)).length;
              return (
                <button key={tg} type="button" onClick={() => setTagFilter(tg)}
                  style={chipStyle(tagFilter === tg)}>{tg} <span style={{ opacity: 0.7 }}>{count}</span></button>
              );
            })}
          </div>
          {/* Outreach status row */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: LIGHT, marginRight: 4 }}>Outreach:</span>
            {['all', 'contacted', 'pending', 'no_valid_contact', 'opted_out'].map(s => (
              <button key={s} type="button" onClick={() => setStatusFilter(s)}
                style={chipStyle(statusFilter === s)}>
                {s === 'all' ? 'All' : STATUS_LABEL[s]} <span style={{ opacity: 0.7 }}>{counts[s] || 0}</span>
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <Card><div style={{ color: LIGHT, padding: 24, textAlign: 'center' }}>Loading report…</div></Card>
      ) : filtered.length === 0 ? (
        <Card><div style={{ color: LIGHT, padding: 40, textAlign: 'center' }}>
          {rows.length === 0
            ? `No leads tagged "${tag}" yet.`
            : `No leads match the "${STATUS_LABEL[statusFilter]}" filter.`}
        </div></Card>
      ) : (
        <Card padded={false}>
          <div style={{ overflowX: 'auto' }}>
            <table style={t.table}>
              <thead>
                <tr>
                  <th style={t.th}>Address</th>
                  <th style={t.th}>Owner</th>
                  <th style={t.th}>JV status</th>
                  <th style={t.th}>Notes (visible to partner)</th>
                  <th style={t.th}>Phone contacted</th>
                  <th style={t.th}>Delivery</th>
                  <th style={t.th}>Tags</th>
                  <th style={t.thNum}>Attempts</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id} style={t.tr}>
                    <td style={t.td}>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{r.addr.split(',')[0] || '—'}</div>
                      <div style={{ fontSize: 11, color: LIGHT }}>{[r.city, r.state].filter(Boolean).join(', ')}</div>
                    </td>
                    <td style={t.td}>{r.ownerName}</td>
                    <td style={t.td}><Pill color={r.jvColor}>{r.jvStatus}</Pill></td>
                    <td style={{ ...t.td, fontSize: 12, color: '#4B5563', maxWidth: 300, lineHeight: 1.5 }}>{r.jvNotes}</td>
                    <td style={{ ...t.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}>
                      {r.lastPhone ? formatPhone(r.lastPhone) : <span style={{ color: LIGHT }}>—</span>}
                    </td>
                    <td style={t.td}>
                      {r.phoneStatus === 'delivered' && <Pill color="green">Delivered</Pill>}
                      {r.phoneStatus === 'failed' && <Pill color="red">Failed</Pill>}
                      {r.phoneStatus === 'opted_out' && <Pill color="gray">Opted out</Pill>}
                      {!r.phoneStatus && <span style={{ color: LIGHT, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={t.td}>
                      {r.tags.length > 0
                        ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {r.tags.map((tg, i) => <Pill key={i} color={tg === 'JV' ? 'gold' : 'gray'}>{tg}</Pill>)}
                          </div>
                        : <span style={{ color: LIGHT, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={t.tdNum}>{r.attemptCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function chipStyle(active) {
  return {
    padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    border: `1px solid ${active ? '#6B1E2E' : '#E5E7EB'}`,
    background: active ? '#6B1E2E' : '#fff',
    color: active ? '#fff' : '#4A1220'
  };
}

// Mirror of the getJvStatus server-side computation so the internal
// Reports page and the JV public page always agree.
function computeJvView(l) {
  const attempts = Array.isArray(l.outreachAttempts) ? l.outreachAttempts : [];
  const last = attempts[attempts.length - 1] || null;
  const responded = !!l.responded;
  const optedOut = !!l.optedOut;
  const fmt = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  let autoNote = '';
  // Order matters — opt-out overrides any prior reply.
  if (optedOut) autoNote = 'Deal is dead. Seller does not want to be contacted.';
  else if (responded) autoNote = `Seller replied ${fmt(l.respondedAt)}. Follow up ASAP.`;
  else if (last) {
    const on = fmt(last.at);
    autoNote = last.ok
      ? `${last.kind === 'followup' ? 'Follow-up' : 'Initial text'} sent to ${last.phone || 'primary'} on ${on}.`
      : `Number ${last.phone || 'primary'} bounced${on ? ` on ${on}` : ''}. Retrying next number on next batch.`;
  } else {
    autoNote = 'Queued for next outreach batch.';
  }
  let autoStatus = 'Queued', autoColor = 'gray';
  if (optedOut) { autoStatus = 'Dead Deal · Not interested'; autoColor = 'red'; }
  else if (responded) { autoStatus = 'Interested · Replied'; autoColor = 'green'; }
  else if (l.outreachStatus === 'no_valid_contact') { autoStatus = 'All numbers exhausted'; autoColor = 'red'; }
  else if (l.lastInitialSentAt) { autoStatus = 'Text sent · Awaiting reply'; autoColor = 'gold'; }
  return {
    status: l.jvStatusOverride || autoStatus,
    color:  l.jvStatusColorOverride || autoColor,
    notes:  l.jvNotesOverride || autoNote
  };
}

function formatPhone(p) {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : p;
}

const t = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: "'Inter', sans-serif" },
  th: { textAlign: 'left', padding: '12px 16px', fontSize: 11, fontWeight: 600, color: LIGHT, borderBottom: `1px solid ${BORDER}`, background: '#FAFAFA' },
  thNum: { textAlign: 'right', padding: '12px 16px', fontSize: 11, fontWeight: 600, color: LIGHT, borderBottom: `1px solid ${BORDER}`, background: '#FAFAFA' },
  tr: {},
  td: { padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, color: '#374151', verticalAlign: 'top' },
  tdNum: { padding: '12px 16px', borderBottom: `1px solid ${BORDER}`, color: '#374151', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
};
